import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface Package {
  id: number;
  name: string;
  name_ar?: string;
  description?: string;
  description_ar?: string;
  price: number | string;
  currency: string;
  features: string[];
  features_ar?: string[];
  processing_time?: string;
  processing_time_ar?: string;
  popular: boolean;
  country_name: string;
  country_name_ar?: string;
  country_flag: string;
}

interface Country {
  name: string;
  englishName: string;
  flag: string;
  code: string;
  packages: Package[];
}

interface CountryPackageSelectorProps {
  packages: Package[];
  selectedPackageId: number;
  onPackageSelect: (packageId: number) => void;
}

const CountryPackageSelector: React.FC<CountryPackageSelectorProps> = ({
  packages,
  selectedPackageId,
  onPackageSelect,
}) => {
  const { t, i18n } = useTranslation();
  const [selectedCountry, setSelectedCountry] = useState<string>('');

  // Helper function to get text based on current language
  const getLocalizedText = (textEn: string, textAr?: string) => {
    return i18n.language === 'ar' && textAr ? textAr : textEn;
  };

  // Helper function to get package name based on language
  const getPackageName = (pkg: Package) => {
    return getLocalizedText(pkg.name, pkg.name_ar);
  };

  // Helper function to get package features based on language
  const getPackageFeatures = (pkg: Package) => {
    return i18n.language === 'ar' && pkg.features_ar ? pkg.features_ar : pkg.features;
  };

  // Helper function to get localized country name
  const getCountryName = (pkg: Package) => {
    return getLocalizedText(pkg.country_name, pkg.country_name_ar);
  };


  const countriesWithPackages = useMemo(() => {
    const countryMap = new Map<string, Country>();

    packages.forEach(pkg => {
      const localizedCountryName = getCountryName(pkg);
      if (!countryMap.has(pkg.country_name)) {
        countryMap.set(pkg.country_name, {
          name: localizedCountryName,
          englishName: pkg.country_name, // Store English name as identifier
          flag: pkg.country_flag,
          code: pkg.country_name.toLowerCase().replace(/\s+/g, '_'),
          packages: []
        });
      }
      countryMap.get(pkg.country_name)!.packages.push(pkg);
    });

    return Array.from(countryMap.values()).sort((a, b) =>
      i18n.language === 'ar'
        ? a.name.localeCompare(b.name, 'ar')
        : a.name.localeCompare(b.name)
    );
  }, [packages, i18n.language]);

  const filteredPackages = useMemo(() => {
    if (!selectedCountry) return [];
    const country = countriesWithPackages.find(c => c.englishName === selectedCountry);
    return country ? country.packages : [];
  }, [selectedCountry, countriesWithPackages]);

  // Memoized localized name for selected country - depends on language
  const selectedCountryLocalizedName = useMemo(() => {
    if (!selectedCountry) return '';

    // Find the country object from our countries list using English name
    const country = countriesWithPackages.find(c => c.englishName === selectedCountry);
    if (!country || !country.packages.length) return selectedCountry;

    // Get the current localized name dynamically based on current language
    return getCountryName(country.packages[0]);
  }, [selectedCountry, countriesWithPackages, i18n.language]);

  const handleCountrySelect = (englishCountryName: string) => {
    setSelectedCountry(englishCountryName);
    // Clear package selection when changing country
    if (selectedPackageId) {
      const selectedPackage = packages.find(p => p.id === selectedPackageId);
      if (selectedPackage && selectedPackage.country_name !== englishCountryName) {
        onPackageSelect(0); // Reset selection
      }
    }
  };

  return (
    <div>
      {/* Title removed - handled by parent component */}
      
      {/* Country Selection */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold mb-4">{t('requestWizard.chooseCountry')}</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {countriesWithPackages.map((country) => (
            <button
              key={country.code}
              onClick={() => handleCountrySelect(country.englishName)}
              className={`
                p-4 border-2 rounded-lg transition-all hover:shadow-md
                ${selectedCountry === country.englishName 
                  ? 'border-blue-500 bg-blue-50 shadow-md' 
                  : 'border-gray-200 hover:border-blue-300'
                }
              `}
            >
              <div className="flex flex-col items-center space-y-2">
                <span className="text-sm font-medium text-center">{country.name}</span>
                <span className="text-xs text-gray-500">
                  {country.packages.length} {country.packages.length === 1 ? t('requestWizard.package') : t('requestWizard.packages')}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Package Selection for Selected Country */}
      {selectedCountry && (
        <div>
          <h3 className="text-lg font-semibold mb-4">
            {t('requestWizard.availablePackagesIn')} {selectedCountryLocalizedName}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPackages.map((pkg) => (
              <div
                key={pkg.id}
                onClick={() => onPackageSelect(pkg.id)}
                className={`
                  border-2 rounded-lg p-6 cursor-pointer transition-all
                  ${selectedPackageId === pkg.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}
                  ${!!pkg.popular ? 'ring-2 ring-yellow-400' : ''}
                `}
              >
                {!!pkg.popular && (
                  <div className="bg-yellow-400 text-black px-2 py-1 rounded text-xs font-medium mb-2 inline-block">
                    {t('requestWizard.recommended')}
                  </div>
                )}
                <div className="flex items-center mb-2">
                  <h4 className="text-lg font-semibold">{getPackageName(pkg)}</h4>
                </div>
                <p className="text-gray-600 mb-2">{getCountryName(pkg)}</p>
                <div className="text-2xl font-bold text-blue-600 mb-4">
                  ${typeof pkg.price === 'number' ? pkg.price.toFixed(2) : pkg.price} {pkg.currency}
                </div>
                <ul className="text-sm space-y-1">
                  {getPackageFeatures(pkg).map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Instruction text when no country selected */}
      {!selectedCountry && (
        <div className="text-center py-12 text-gray-500">
          <div className="text-4xl mb-4">🌍</div>
          <p className="text-lg">{t('requestWizard.pleaseSelectCountry')}</p>
        </div>
      )}
    </div>
  );
};

export default CountryPackageSelector;